import { Fragment, useEffect, useRef, useState } from 'react';
import { getFormattedDateTimeLocal } from 'utils/getFormattedDate';
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Badge,
  Box,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Paper,
  Popper,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { CommentOutlined, MessageOutlined } from '@ant-design/icons';
import { ThemeMode } from 'types/config';
import { useStreamChat } from 'contexts/StreamChatContext';
import useAuth from 'hooks/useAuth';
import { Channel } from 'stream-chat';

const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

interface ChannelData {
  id: string;
  name: string;
  unreadCount: number;
  lastMessageAt: Date;
  mcaId?: string;
}

const StreamChannels = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const { user: currentUser } = useAuth();
  const { client, isConnected, isLoading } = useStreamChat();
  const anchorRef = useRef<any>(null);
  const [totalUnreadCount, setTotalUnreadCount] = useState<number>(0);
  const [channels, setChannels] = useState<ChannelData[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [connectionEvent, setConnectionEvent] = useState<number>(0);

  const handleToggle = () => {
    setOpen((prevOpen: boolean) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

  useEffect(() => {
    if (!client || !isConnected || !currentUser || !client.user) {
      setChannels([]);
      setTotalUnreadCount(0);
      return;
    }

    let mounted = true;
    let watchedChannels: Channel[] = [];
    const eventHandlersMap = new Map<string, any>();

    const updateChannelsAndCounts = async () => {
      if (!client.user || !isConnected) {
        console.warn('Client not connected, skipping channel update');
        return;
      }
      try {
        const channelQueryResponse = await client.queryChannels(
          {
            members: { $in: [currentUser.id] },
            type: 'messaging'
          },
          { last_message_at: -1 },
          {
            limit: 100,
            watch: true,  // Enable websocket subscriptions
            state: true   // Include channel state
          }
        );

        if (!mounted) return;

        if (!channelQueryResponse?.length) {
          setChannels([]);
          setTotalUnreadCount(0);
          return;
        }

        // Store actual channel instances (filter out notifications channel)
        watchedChannels = channelQueryResponse.filter(
          (channel) => channel.id !== `notifications-${currentUser.id}`
        );

        // Setup event listeners on actual channel instances
        setupEventListeners(watchedChannels);

        // Extract channel data for UI (notifications already filtered out)
        const validChannels = watchedChannels
          .map((channel): ChannelData | null => {
            try {
              const unreadCount = channel.countUnread();
              const lastMessage = channel.state.messages[channel.state.messages.length - 1];
              const mcaId = (channel.data as any)?.mcaId;
              const channelName = (channel.data as any)?.name;

              return {
                id: channel.id || '',
                name: channelName || channel.id,
                unreadCount,
                lastMessageAt: lastMessage?.created_at ? new Date(lastMessage.created_at) : new Date(),
                mcaId
              } as ChannelData;
            } catch (error) {
              console.warn('Error processing channel:', error);
              return null;
            }
          })
          .filter((ch): ch is ChannelData => ch !== null);

        const totalUnread = validChannels.reduce((sum, ch) => sum + ch.unreadCount, 0);

        setChannels(validChannels);
        setTotalUnreadCount(totalUnread);

      } catch (error) {
        console.error('Failed to update channels:', error);
        if (mounted) {
          setChannels([]);
          setTotalUnreadCount(0);
        }
      }
    };

    const setupEventListeners = (channelInstances: Channel[]) => {
      channelInstances.forEach((channel) => {
        const updateChannelCounts = () => {
          if (!mounted) return;

          setChannels((prev) => {
            const updated = prev.map((ch) =>
              ch.id === channel.id
                ? { ...ch, unreadCount: channel.countUnread() }
                : ch
            );
            setTotalUnreadCount(updated.reduce((sum, ch) => sum + ch.unreadCount, 0));
            return updated;
          });
        };

        const updateChannelWithNewMessage = (event: any) => {
          if (!mounted) return;

          setChannels((prev) => {
            const updated = prev.map((ch) =>
              ch.id === channel.id
                ? {
                  ...ch,
                  unreadCount: channel.countUnread(),
                  lastMessageAt: event.message?.created_at ? new Date(event.message.created_at) : ch.lastMessageAt
                }
                : ch
            );
            setTotalUnreadCount(updated.reduce((sum, ch) => sum + ch.unreadCount, 0));
            return updated;
          });
        };

        // Store handler references for proper cleanup
        eventHandlersMap.set(channel.id!, {
          messageNewHandler: updateChannelWithNewMessage,
          messageReadHandler: updateChannelCounts,
          messageDeletedHandler: updateChannelCounts
        });

        // Attach listeners to actual channel instances
        channel.on('message.new', updateChannelWithNewMessage);
        channel.on('message.read', updateChannelCounts);
        channel.on('message.deleted', updateChannelCounts);
      });
    };

    const handleChannelCreated = (event: any) => {
      if (!mounted || !event.channel) return;

      const isCurrentUserMember = event.channel.members?.some((member: any) =>
        member.user_id === currentUser.id || member.user?.id === currentUser.id
      );

      if (isCurrentUserMember && event.channel.type === 'messaging') {
        // Add new channel immediately to UI
        const newChannelData: ChannelData = {
          id: event.channel.id,
          name: event.channel.data?.name || event.channel.id,
          unreadCount: 0,
          lastMessageAt: new Date(event.channel.created_at || Date.now()),
          mcaId: event.channel.data?.mcaId
        };

        setChannels(prev => {
          if (prev.some(ch => ch.id === newChannelData.id)) {
            return prev;
          }
          return [newChannelData, ...prev];
        });

        // Refresh to get the full channel with proper event listeners
        setTimeout(() => {
          if (mounted) {
            updateChannelsAndCounts();
          }
        }, 500);
      }
    };

    // Setup global listeners
    client.on('channel.created', handleChannelCreated);

    // Initial load
    updateChannelsAndCounts();

    return () => {
      mounted = false;

      // Cleanup global listeners
      client.off('channel.created', handleChannelCreated);

      // Cleanup channel-specific listeners with proper handler references
      watchedChannels.forEach((channel) => {
        const handlers = eventHandlersMap.get(channel.id!);
        if (handlers) {
          try {
            channel.off('message.new', handlers.messageNewHandler);
            channel.off('message.read', handlers.messageReadHandler);
            channel.off('message.deleted', handlers.messageDeletedHandler);
          } catch (error) {
            console.warn(`Failed to cleanup listeners for channel ${channel.id}:`, error);
          }
        }
      });

      watchedChannels = [];
      eventHandlersMap.clear();
    };
  }, [client, isConnected, currentUser?.id, connectionEvent]);

  useEffect(() => {
    if (!client || !currentUser) return;

    const handleStreamReady = () => {
      setConnectionEvent(prev => prev + 1);
    };

    client.on("connection.recovered", handleStreamReady);
    client.on("connection.changed", handleStreamReady);

    return () => {
      client.off("connection.recovered", handleStreamReady);
      client.off("connection.changed", handleStreamReady);
    };
  }, [client, currentUser]);

  const handleChannelClick = async (channel: ChannelData) => {
    if (channel.mcaId) {
      setOpen(false);
      navigate(`/merchants/mca/${channel.mcaId}?openChat=true`);
    }
  };

  const extractMcaName = (channelName: string) => {
    const parts = channelName.split('---');
    if (parts.length >= 3) {
      return parts[2].replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    }
    return channelName;
  };

  if (isLoading) {
    return null;
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : 'transparent' }}
        aria-label="open stream channels"
        ref={anchorRef}
        aria-controls={open ? 'stream-channels-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={totalUnreadCount} color="primary">
          <CommentOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? -5 : 0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: '100%',
                minWidth: 285,
                maxWidth: 420,
                [theme.breakpoints.down('md')]: {
                  maxWidth: 285
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard title= {!channels.length ? "No active chats yet." : "Active Chats"} elevation={0} border={false} content={false}>
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { position: 'relative' }
                      }
                    }}
                  >
                    {channels.map((channel) => (
                      <Fragment key={channel.id}>
                        <ListItemButton onClick={() => handleChannelClick(channel)} disabled={!channel.mcaId}>
                          <ListItemAvatar>
                            <Badge
                              badgeContent={channel.unreadCount}
                              color="secondary"
                              invisible={channel.unreadCount === 0}
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.75rem',
                                  height: 18,
                                  minWidth: 18
                                }
                              }}
                            >
                              <Avatar
                                sx={{
                                  color: channel.unreadCount > 0 ? 'primary.contrastText' : 'primary.main',
                                  bgcolor: channel.unreadCount > 0 ? 'primary.main' : 'primary.lighter'
                                }}
                              >
                                <MessageOutlined />
                              </Avatar>
                            </Badge>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="h6">
                                <Typography component="span" variant="subtitle1">
                                  {extractMcaName(channel.name)}
                                </Typography>
                              </Typography>
                            }
                            secondary={getFormattedDateTimeLocal(channel.lastMessageAt)}
                          />
                        </ListItemButton>
                        <Divider />
                      </Fragment>
                    ))}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default StreamChannels;